<template>
  <canvas ref="canvasRef" class="pixel-bg" aria-hidden="true"></canvas>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount } from 'vue'

/*
  Pixelated — site-wide "pixel waves" background (calm edition).
  Soft amber wave-bands roll slowly across a pixel grid behind the whole
  site, with a gentle cursor ripple. Tuned to stay in the background:
  low opacity, slow movement, and cheap to render.

  Performance:
  - canvas renders at DPR 1 (pixels are blocky by design)
  - frame rate capped (~24 fps) — this is ambience, not a game
  - wave field is separable: sin() per row/column/diagonal instead of per
    cell (≈150 sin-calls per frame instead of thousands)
  - pauses when the tab is hidden; respects prefers-reduced-motion
*/

const canvasRef = ref(null)

const ACCENT = [250, 168, 43]   // #FAA82B
const CELL = 28                 // px per pixel-block
const WAVE_MAX = 0.14           // peak alpha of a wave crest (keep subtle)
const FRAME_MS = 42             // ~24 fps cap
const SPARK_RATIO = 0.003       // fraction of cells twinkling as sparks

let ctx, raf
let W = 0, H = 0
let cols = 0, rows = 0
let running = true
let lastFrame = 0

let jitter = new Float32Array(0) // per-cell brightness multiplier (texture)
let dead = new Uint8Array(0)     // per-cell skip flag (pixelated gaps)
let colSin = new Float32Array(0)
let rowSin = new Float32Array(0)
let diagSin = new Float32Array(0)
const sparks = new Map()         // key -> { a, dir, max, speed }
const heat = new Map()           // key -> intensity 0..1 (mouse ripple)

let seed = 1
function rnd() { return typeof Math.random === 'function' ? Math.random() : ((Math.sin(seed++) + 1) / 2) }

function key(cx, cy) { return cy * cols + cx }

function resize() {
  const canvas = canvasRef.value
  if (!canvas) return
  const rect = canvas.getBoundingClientRect()
  W = rect.width
  H = rect.height
  // DPR 1 on purpose: blocky pixels + 4x less work than retina
  canvas.width = Math.round(W)
  canvas.height = Math.round(H)
  ctx.setTransform(1, 0, 0, 1, 0, 0)
  cols = Math.ceil(W / CELL)
  rows = Math.ceil(H / CELL)

  const n = cols * rows
  jitter = new Float32Array(n)
  dead = new Uint8Array(n)
  for (let i = 0; i < n; i++) {
    jitter[i] = 0.55 + rnd() * 0.7
    dead[i] = rnd() < 0.18 ? 1 : 0
  }
  colSin = new Float32Array(cols)
  rowSin = new Float32Array(rows)
  diagSin = new Float32Array(cols + rows)
  sparks.clear()
  heat.clear()
}

function drawCell(cx, cy, alpha) {
  if (alpha <= 0.02) return
  ctx.fillStyle = `rgba(${ACCENT[0]},${ACCENT[1]},${ACCENT[2]},${Math.min(alpha, 0.7)})`
  ctx.fillRect(cx * CELL + 2, cy * CELL + 2, CELL - 4, CELL - 4)
}

function spawnSpark() {
  const cx = Math.floor(rnd() * cols)
  const cy = Math.floor(rnd() * rows)
  const k = key(cx, cy)
  if (sparks.has(k)) return
  sparks.set(k, {
    a: 0,
    dir: 1,
    max: 0.18 + rnd() * 0.22,
    speed: 0.002 + rnd() * 0.005,
  })
}

/* Precompute the separable wave terms for this frame (slow drift). */
function computeWaveTables(t) {
  for (let cx = 0; cx < cols; cx++) colSin[cx] = Math.sin(cx * 0.29 + t * 0.00042)
  for (let cy = 0; cy < rows; cy++) rowSin[cy] = Math.sin(cy * 0.38 - t * 0.00033)
  const dmax = cols + rows
  for (let d = 0; d < dmax; d++) diagSin[d] = Math.sin(d * 0.21 + t * 0.00055)
}

function drawWaves() {
  for (let cy = 0; cy < rows; cy++) {
    const rs = rowSin[cy]
    const rowBase = cy * cols
    for (let cx = 0; cx < cols; cx++) {
      const i = rowBase + cx
      if (dead[i]) continue
      // normalize sum of 3 sinusoids to 0..1, cube to sharpen crests
      const n = (colSin[cx] + rs + diagSin[cx + cy] + 3) / 6
      const a = n * n * n * WAVE_MAX * jitter[i]
      drawCell(cx, cy, a)
    }
  }
}

function frame(t) {
  if (!running) return
  raf = requestAnimationFrame(frame)
  if (t - lastFrame < FRAME_MS) return
  lastFrame = t

  ctx.clearRect(0, 0, W, H)

  computeWaveTables(t)
  drawWaves()

  // sparse, slow twinkles on top
  const targetSparks = Math.max(4, Math.round(cols * rows * SPARK_RATIO))
  let guard = 0
  while (sparks.size < targetSparks && guard++ < 4) spawnSpark()
  for (const [k, s] of sparks) {
    s.a += s.dir * s.speed * FRAME_MS
    if (s.a >= s.max) { s.a = s.max; s.dir = -1 }
    if (s.a <= 0 && s.dir === -1) { sparks.delete(k); continue }
    drawCell(k % cols, Math.floor(k / cols), s.a)
  }

  // gentle mouse ripple
  for (const [k, h] of heat) {
    drawCell(k % cols, Math.floor(k / cols), h * 0.32)
    const next = h * 0.92
    if (next < 0.015) heat.delete(k)
    else heat.set(k, next)
  }
}

function onPointerMove(e) {
  const ccx = Math.floor(e.clientX / CELL)
  const ccy = Math.floor(e.clientY / CELL)
  const R = 2 // cells radius
  for (let dy = -R; dy <= R; dy++) {
    for (let dx = -R; dx <= R; dx++) {
      const cx = ccx + dx
      const cy = ccy + dy
      if (cx < 0 || cy < 0 || cx >= cols || cy >= rows) continue
      const d = Math.sqrt(dx * dx + dy * dy)
      if (d > R) continue
      const k = key(cx, cy)
      const add = (1 - d / (R + 0.5)) * 0.5
      heat.set(k, Math.min(1, (heat.get(k) || 0) + add))
    }
  }
}

function onVisibility() {
  if (document.hidden) {
    running = false
    if (raf) cancelAnimationFrame(raf)
  } else if (!running) {
    running = true
    lastFrame = 0
    raf = requestAnimationFrame(frame)
  }
}

let ro
onMounted(() => {
  const canvas = canvasRef.value
  ctx = canvas.getContext('2d')
  resize()

  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches
  if (reduced) {
    // one static calm frame, no animation
    running = false
    computeWaveTables(0)
    drawWaves()
    return
  }

  raf = requestAnimationFrame(frame)
  window.addEventListener('mousemove', onPointerMove, { passive: true })
  document.addEventListener('visibilitychange', onVisibility)

  if ('ResizeObserver' in window) {
    ro = new ResizeObserver(() => resize())
    ro.observe(canvas)
  } else {
    window.addEventListener('resize', resize)
  }
})

onBeforeUnmount(() => {
  running = false
  if (raf) cancelAnimationFrame(raf)
  window.removeEventListener('mousemove', onPointerMove)
  document.removeEventListener('visibilitychange', onVisibility)
  if (ro) ro.disconnect()
  else window.removeEventListener('resize', resize)
})
</script>

<style scoped>
/* Fixed behind the entire site; body is transparent and sections are
   transparent so the page reads as one continuous surface. */
.pixel-bg {
  position: fixed;
  inset: 0;
  width: 100vw;
  height: 100vh;
  display: block;
  pointer-events: none;
  z-index: -1;
}
</style>
